/**
 * The shared visual vocabulary. Every screen imports from here.
 *
 * One file rather than the usual file-per-component, deliberately: this is the
 * contract several screens are built against at once, and a single import
 * surface (`from "@/components/ui"`) is what stops each screen inventing its
 * own button. Split it per file when it outgrows one screenful of scrolling.
 *
 * Nothing here knows the domain. Anything that mentions an event, a record or
 * a learner belongs in a feature component, not in this file.
 */

import { cva, type VariantProps } from "class-variance-authority"
import {
  AlertCircle,
  CalendarDays,
  Check,
  ChevronDown,
  Clock,
  Inbox,
  Loader2,
} from "lucide-react"
import * as React from "react"

import { cn, splitRands } from "@/lib/utils"

import {
  PICKER_SCALE,
  PickerPopup,
  popupWidth,
  type PickerType,
} from "./date-picker"
import { shouldAlignEnd, shouldDropUp } from "./popup-placement"

// --- Button ----------------------------------------------------------------

const button = cva(
  // The transition list is explicit so the scale on press and the shadow on
  // hover animate while layout properties never do. The press scale is subtle
  // on purpose: acknowledgement, not bounce.
  //
  // NO FOCUS CLASSES HERE, and that is the point. This list carried
  // `focus-visible:outline-none` plus a translucent `ring-ring/40` substitute
  // until 2026-08-05, which cancelled the one global rule in `styles.css` and
  // replaced it with an indicator that measured 1.71:1 to 1.97:1 on rendered
  // pixels: an alpha ring on this palette cannot reach the 3:1 WCAG 2.1 asks of
  // a focus indicator at ANY alpha below 1, so it was not a ring that could be
  // tuned to pass. Deleting the five classes hands the button back to the
  // global outline, measured at 4.02:1 to 5.02:1 light and 5.06:1 to 5.54:1
  // dark. The law is recorded beside that rule: a component must not opt out.
  //
  // `aria-disabled` is dimmed alongside `disabled` because the loading state
  // below is aria-disabled rather than disabled, and a busy button must still
  // read as unavailable.
  "inline-flex items-center justify-center gap-2 rounded-lg text-sm font-medium cursor-pointer transition-[color,background-color,border-color,box-shadow,transform] duration-150 ease-out active:scale-[0.985] disabled:pointer-events-none disabled:opacity-50 aria-disabled:opacity-50 aria-disabled:active:scale-100 whitespace-nowrap",
  {
    variants: {
      variant: {
        // Every colour is a semantic token. A literal like `bg-white` reads
        // fine until the theme flips, and then it is a white card on a black
        // page with invisible text on it.
        // The primary hover is a SOLID token, never the fill at an alpha. An
        // alpha fill blends towards whatever is behind the button, which on a
        // white card put white-on-red at 4.49:1, under AA, in the one state no
        // screenshot catches. The arithmetic and both themes' values are
        // recorded at `--brand-fill-hover` in `styles.css`.
        primary:
          "bg-primary text-primary-foreground shadow-xs hover:bg-primary-hover",
        secondary:
          "bg-card text-foreground border border-border shadow-xs hover:bg-muted hover:border-border-strong",
        ghost: "text-foreground hover:bg-muted",
        // Measured alongside primary on 2026-08-05 and deliberately left on the
        // alpha: danger's fill is dark enough that its hover still measures
        // 6.48:1 light and 5.36:1 dark on rendered pixels, so it is not a
        // defect. It is the same mechanism though, so if `--destructive` is
        // ever lightened, measure this hover before shipping it.
        danger:
          "bg-destructive text-destructive-foreground shadow-xs hover:bg-destructive/90",
      },
      size: {
        // 44px minimum on the touch target: the design brief asks for
        // touch-friendly controls and WCAG 2.1 AA.
        md: "h-11 px-4 py-2",
        sm: "h-9 px-3 text-sm",
        icon: "h-11 w-11",
      },
    },
    defaultVariants: { variant: "primary", size: "md" },
  },
)

/**
 * The button's classes on their own, for the rare thing that must be a real
 * anchor rather than a `<button>`: a navigation a user should be able to
 * middle-click or open in a new tab is a link, whatever it looks like.
 */
export const buttonClasses = button

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof button> {
  loading?: boolean
}

/**
 * A button, and one rule about what "busy" means.
 *
 * LOADING IS `aria-disabled`, NOT `disabled`, and the difference is the whole
 * of this component's interesting behaviour. It was `disabled={disabled ||
 * loading}` until 2026-08-05, which is the idiom every React codebase uses and
 * which quietly breaks the keyboard: the browser BLURS an element the moment it
 * becomes disabled, and focus falls to `<body>`. So a keyboard user who pressed
 * Save was thrown to the top of the document, with no focus indicator anywhere,
 * after EVERY async action in the product: save, sign off, apply an import,
 * advance the wizard. Nothing brought them back, because the button they had
 * been standing on was a different element by the time it re-enabled.
 *
 * `aria-disabled` keeps the element focusable, so focus never moves and there
 * is nothing to restore. That is why it is preferred over the alternative fix,
 * which is to remember the active element and re-focus it once loading ends: a
 * refocus hack races the re-render, fights any focus the caller moves
 * deliberately, and is a mechanism that has to keep working rather than a state
 * that cannot go wrong. This is also what the WAI-ARIA APG recommends for a
 * control that must stay findable while it is unavailable.
 *
 * The price of `aria-disabled` is that the browser stops enforcing anything, so
 * the guard below is the enforcement: a click is swallowed, Enter and Space are
 * swallowed, and a submit button submits nothing. `pointer-events-none` is
 * deliberately NOT used for it, tempting as it looks - it would let the click
 * fall through to whatever is underneath, and this product has already been bitten
 * once by a click reaching a row's navigation from a control on top of it.
 *
 * A caller's own `disabled` is untouched and still genuinely disables: "not
 * available at all" and "busy, and still here" are different assertions, and a
 * Cancel beside a saving Confirm wants the first one.
 */
export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    { className, variant, size, loading, disabled, children, onClick, onKeyDown, ...props },
    ref,
  ) => {
    const busy = Boolean(loading) && !disabled
    return (
      <button
        ref={ref}
        className={cn(button({ variant, size }), className)}
        disabled={disabled}
        aria-disabled={busy || undefined}
        aria-busy={busy || undefined}
        onClick={(event) => {
          if (busy) {
            // Both, and both matter: `preventDefault` is what stops a submit
            // button submitting its form, and `stopPropagation` is what stops
            // the swallowed click being read as a click on the row or the
            // backdrop behind it.
            event.preventDefault()
            event.stopPropagation()
            return
          }
          onClick?.(event)
        }}
        onKeyDown={(event) => {
          // A button fires its click from Enter and Space, so those are the two
          // keys that have to be intercepted before the browser synthesises it.
          if (busy && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault()
            event.stopPropagation()
            return
          }
          onKeyDown?.(event)
        }}
        {...props}
      >
        {loading && <Loader2 className="h-4 w-4 animate-spin" aria-hidden />}
        {children}
      </button>
    )
  },
)
Button.displayName = "Button"

// --- Form controls ---------------------------------------------------------

// Focus is the GLOBAL outline plus a border turn and a soft inner ring: the
// outline is the indicator, and the other two are the form control's own way of
// saying the same thing from the inside. This list carried
// `focus-visible:outline-none` until 2026-08-05, which made the translucent
// `ring-ring/25` the whole indicator - and a 25% ring measures 1.81:1 at its
// theoretical ceiling, which is pure black, so no colour could have saved it.
// Dropping the one class restores the outline; everything else here is
// deliberately unchanged, so the invalid state keeps the destructive ring it
// has always shown while focused.
//
// `aria-invalid` comes from `Field` wiring a 422 to its control, so a failed
// field shows its state on the control itself, not only in the message under it.
const field =
  "h-11 w-full rounded-lg border border-input bg-card text-foreground px-3 py-2 text-sm placeholder:text-muted-foreground shadow-xs transition-[color,box-shadow,border-color] duration-150 hover:border-border-strong focus-visible:border-ring focus-visible:ring-2 focus-visible:ring-ring/25 aria-[invalid=true]:border-destructive aria-[invalid=true]:ring-destructive/20 disabled:cursor-not-allowed disabled:opacity-50 disabled:bg-muted/50"

export const Input = React.forwardRef<HTMLInputElement, React.ComponentProps<"input">>(
  ({ className, ...props }, ref) => (
    <input ref={ref} className={cn(field, className)} {...props} />
  ),
)
Input.displayName = "Input"

export const Textarea = React.forwardRef<
  HTMLTextAreaElement,
  React.ComponentProps<"textarea">
>(({ className, ...props }, ref) => (
  <textarea ref={ref} className={cn(field, "h-auto min-h-24", className)} {...props} />
))
Textarea.displayName = "Textarea"

/**
 * A date, datetime or time field: a native input for the typing, our own
 * popup for the picking.
 *
 * `showPicker()` is gone. Dawid ruled on 2026-08-04 (R5) that the date and
 * time pickers become scripted on the listbox pattern, superseding the
 * 2026-07-30 decision to keep the native popup, because the review meeting
 * found it opening on nothing rather than on the date already in the field,
 * staying up after a day was chosen, and offering a time spinner nobody could
 * read. None of that is reachable from CSS, so the popup is now ours and lives
 * in `date-picker.tsx`.
 *
 * The FIELD is deliberately still a native input. That is what keeps typing,
 * the segment grammar, the browser's own ISO value and the closed field's
 * arrow keys exactly as they were, and it is why no call site changed. The
 * icon is still a real button and still out of the tab order, because the
 * input is the control and the keyboard opens the popup from it.
 *
 * `className` lands on the wrapper, the same contract as `Select`.
 */
export const PickerInput = React.forwardRef<
  HTMLInputElement,
  React.ComponentProps<"input">
>(({ className, type = "date", disabled, onChange, onKeyDown, onFocus, ...props }, ref) => {
  const kind: PickerType =
    type === "time" ? "time" : type === "datetime-local" ? "datetime-local" : "date"
  const rootRef = React.useRef<HTMLSpanElement>(null)
  const inner = React.useRef<HTMLInputElement>(null)
  React.useImperativeHandle(ref, () => inner.current as HTMLInputElement)

  const [open, setOpen] = React.useState(false)
  const [dropUp, setDropUp] = React.useState(false)
  const [alignEnd, setAlignEnd] = React.useState(false)

  // What the popup opens on. A controlled field's prop is the truth and is
  // what every call site in this product passes; the mirror covers an
  // uncontrolled one, refreshed from the DOM each time the popup opens so it
  // cannot be reading a value the user has since typed over.
  const [mirror, setMirror] = React.useState("")
  const current =
    props.value === undefined || props.value === null ? mirror : String(props.value)

  // The offered times, from the field's own `step` where it declares one.
  // Seconds on the wire, as the HTML attribute is; a quarter hour otherwise.
  const step = Number(props.step)
  const minutes =
    Number.isFinite(step) && step >= 60 && step <= 3600 ? Math.round(step / 60) : 15

  const show = React.useCallback(() => {
    if (disabled) return
    setMirror(inner.current?.value ?? "")
    setDropUp(shouldDropUp(inner.current, PICKER_SCALE.height))
    setAlignEnd(shouldAlignEnd(inner.current, popupWidth(kind)))
    setOpen(true)
  }, [disabled, kind])

  /**
   * The one place a picked value leaves this component.
   *
   * Written through the prototype's own setter and announced with a real
   * `input` event, rather than a synthesised object like the `Select` uses.
   * There is a real input here to write to, so the call site gets the genuine
   * change event it was already written against, carrying `target.value`,
   * `target.id` and `target.name`. Going through the prototype setter is what
   * makes React see the change at all: React installs its own value tracker on
   * the element, and assigning through that tracker would look to it like
   * nothing had happened.
   */
  const commit = React.useCallback((next: string) => {
    const input = inner.current
    if (!input) return
    const setter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, "value")?.set
    setter?.call(input, next)
    input.dispatchEvent(new Event("input", { bubbles: true }))
    setOpen(false)
    input.focus()
  }, [])

  const dismiss = React.useCallback(() => {
    setOpen(false)
    inner.current?.focus()
  }, [])

  // A pointer landing anywhere else closes the popup, without pulling focus
  // back: focus belongs wherever the pointer went. `pointerdown` rather than
  // `click`, so the popup is gone before whatever was clicked reacts.
  React.useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener("pointerdown", onPointerDown)
    return () => document.removeEventListener("pointerdown", onPointerDown)
  }, [open])

  const Icon = kind === "time" ? Clock : CalendarDays
  const label =
    kind === "time"
      ? "Open time picker"
      : kind === "datetime-local"
        ? "Open date and time picker"
        : "Open date picker"

  return (
    <span
      ref={rootRef}
      data-picker-field=""
      className={cn("relative block w-full", className)}
    >
      <input
        ref={inner}
        type={type}
        disabled={disabled}
        className={cn(field, "pr-10")}
        onChange={(event) => {
          setMirror(event.target.value)
          onChange?.(event)
        }}
        onFocus={(event) => {
          onFocus?.(event)
          // Standing in the field means typing, and a popup drawn over the
          // next field while somebody types into this one is chrome in the
          // way. Opening is always a deliberate act: the icon, or a key.
          setOpen(false)
        }}
        onKeyDown={(event) => {
          onKeyDown?.(event)
          if (event.defaultPrevented || disabled) return
          // The ruled listbox grammar, and nothing more: bare arrows are left
          // to the native input, where they step the segment under the caret
          // exactly as Windows does. Chrome was measured on 2026-08-04 to
          // confirm none of these three opens its own picker any more.
          if (event.key === "F4" || event.key === " " || (event.altKey && event.key === "ArrowDown")) {
            event.preventDefault()
            show()
          }
        }}
        {...props}
      />
      <button
        type="button"
        tabIndex={-1}
        disabled={disabled}
        aria-label={label}
        aria-expanded={open}
        aria-haspopup="dialog"
        className="absolute inset-y-0 right-0 grid w-10 cursor-pointer place-items-center rounded-r-lg text-muted-foreground hover:text-foreground"
        onClick={() => {
          if (open) dismiss()
          else show()
        }}
      >
        <Icon className="h-4 w-4" aria-hidden />
      </button>
      {open && (
        <PickerPopup
          type={kind}
          value={current}
          minutes={minutes}
          dropUp={dropUp}
          alignEnd={alignEnd}
          onPick={commit}
          onDismiss={dismiss}
        />
      )}
    </span>
  )
})
PickerInput.displayName = "PickerInput"

// --- Select ----------------------------------------------------------------
//
// The dropdown was a native `<select>` with `appearance-none` until 2026-07-31.
// That fixed the closed control and could never fix the open one: the option
// list is drawn by the operating system, no CSS reaches inside it, and it was
// the last thing on the page that still looked foreign. Dawid ruled it a
// styled listbox everywhere, so the list below is our own DOM.
//
// The trade that ruling accepts, stated plainly because it is what a native
// select was giving away for free: keyboard behaviour, type-ahead and the
// phone's native picker. The first two are re-implemented here to the WAI-ARIA
// APG's select-only combobox pattern; the third is the real loss, and a tap
// now opens this list rather than the platform's wheel.

/** How tall the popup can get before it scrolls, in pixels. Matches `max-h-64`. */
const LIST_MAX = 256

/** The plain text of an option's children, which is what type-ahead matches on. */
function textOf(node: React.ReactNode): string {
  if (node === null || node === undefined || typeof node === "boolean") return ""
  if (typeof node === "string") return node
  if (typeof node === "number") return String(node)
  if (Array.isArray(node)) return node.map(textOf).join("")
  if (React.isValidElement(node)) {
    return textOf((node.props as { children?: React.ReactNode }).children)
  }
  return ""
}

interface SelectItem {
  value: string
  label: React.ReactNode
  text: string
  disabled: boolean
}

/**
 * The `<option>` children a caller wrote, read as data.
 *
 * This is what keeps the rewrite free at the call sites: every screen still
 * writes the markup it wrote against a native select, and nothing here asks
 * for an `items` prop. Fragments and mapped arrays are walked through, since
 * `{list.map(...)}` beside a literal `<option>` is the shape most screens use,
 * and `<optgroup>` is flattened rather than dropped.
 */
function readOptions(children: React.ReactNode): SelectItem[] {
  const items: SelectItem[] = []
  const walk = (node: React.ReactNode) => {
    React.Children.forEach(node, (child) => {
      if (!React.isValidElement(child)) return
      const props = child.props as React.ComponentProps<"option">
      if (child.type === React.Fragment || child.type === "optgroup") {
        walk(props.children)
        return
      }
      if (child.type !== "option") return
      const text = textOf(props.children)
      items.push({
        // A value-less option reports its own text, which is what the DOM
        // does, and `<option>Any</option>` appears in this codebase.
        value: props.value === undefined || props.value === null ? text : String(props.value),
        label: props.children,
        text,
        disabled: Boolean(props.disabled),
      })
    })
  }
  walk(children)
  return items
}

/**
 * A native select's props, minus the ones a scripted listbox cannot honour.
 *
 * Everything but `onChange` is typed against the `<button>` this actually
 * renders, because that is what the attributes land on and pretending
 * otherwise would put a select's event types on a button's events. `onChange`
 * keeps the select signature deliberately: it is the one prop every call site
 * reads, and holding its shape is what made the rewrite cost nothing outside
 * this file.
 *
 * Gone, and none of them in use: `multiple` (this picks one), `size` (the list
 * is never rendered inline) and native form participation, since a button
 * submits no value. Every call site is controlled React state, which is where
 * the values were already coming from.
 */
export interface SelectProps
  extends Omit<
    React.ComponentProps<"button">,
    "ref" | "type" | "role" | "value" | "defaultValue" | "onChange" | "children"
  > {
  /** The chosen option's value. Omit it for an uncontrolled control. */
  value?: string | number | null
  defaultValue?: string | number
  /** Handed a synthesised event carrying `target.value`, as a select would. */
  onChange?: React.ChangeEventHandler<HTMLSelectElement>
  /** `<option>` elements, exactly as a native select takes them. */
  children?: React.ReactNode
  /** Reflected as `aria-required`: there is no native constraint to set. */
  required?: boolean
}

/**
 * A dropdown that looks like the rest of the form controls, open or closed.
 *
 * **The caller's contract is a native select's**, deliberately and exactly:
 * `value`, `onChange` read as `event.target.value`, `disabled`, and `<option>`
 * children. Fourteen screens were written against a `<select>`; thirteen were
 * not touched, and the fourteenth only because its `className` reached past
 * the wrapper with `[&>select]`. `onChange` is handed a synthesised event
 * rather than a real DOM one, carrying `target.value`, `target.name` and
 * `target.id`; that is the whole of what the call sites read, and it is stated
 * here rather than discovered by someone reaching for `event.nativeEvent`.
 *
 * `className` lands on the wrapper rather than the trigger, because the callers
 * that pass one pass a width and the wrapper is what sizes the control. A
 * caller reaching past that writes `[&>button]:...`, as the employee filter
 * pills do.
 *
 * The popup is a child of this component and never a portal. Three of these
 * live inside a native `<dialog>`, which the browser puts in the top layer, so
 * anything portalled to `document.body` would render *underneath* the modal it
 * belongs to. Being a child instead means the popup can be clipped by the
 * modal's scrolling body, which is what the flip-up below is for.
 *
 * ARIA is the APG's select-only combobox: focus stays on the trigger and
 * `aria-activedescendant` names the option being walked, rather than focus
 * roving into the list. That is what keeps `Field`'s injected `id`,
 * `aria-describedby` and `aria-invalid` on the element a keyboard user is
 * actually standing on.
 */
export const Select = React.forwardRef<HTMLButtonElement, SelectProps>(
  (
    {
      className,
      value,
      defaultValue,
      onChange,
      onKeyDown,
      onClick,
      onBlur,
      disabled,
      children,
      id,
      name,
      required,
      ...rest
    },
    ref,
  ) => {
    const options = React.useMemo(() => readOptions(children), [children])

    const [open, setOpen] = React.useState(false)
    const [active, setActive] = React.useState(0)
    const [dropUp, setDropUp] = React.useState(false)

    // Uncontrolled is supported because a native select supports it: with no
    // `value`, the first option is what the control shows. `null` rather than
    // `""` for "nothing chosen yet", because `""` is a real value here: it is
    // what every "All sites" and "Any status" option carries, and treating it
    // as unset would make those the one choice that could not be made.
    const controlled = value !== undefined
    const [inner, setInner] = React.useState<string | null>(() =>
      defaultValue === undefined ? null : String(defaultValue),
    )
    const first = options[0]
    const current = controlled
      ? value === null
        ? ""
        : String(value)
      : (inner ?? (first ? first.value : ""))

    const rootRef = React.useRef<HTMLSpanElement>(null)
    const triggerRef = React.useRef<HTMLButtonElement>(null)
    const listRef = React.useRef<HTMLUListElement>(null)
    React.useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement)

    const listId = React.useId()
    const optionId = (index: number) => `${listId}-option-${index}`

    const currentIndex = options.findIndex((option) => option.value === current)
    const selected = currentIndex >= 0 ? options[currentIndex] : undefined

    /**
     * The one place a value leaves this component.
     *
     * The event is a plain object shaped like the change event a `<select>`
     * would have raised. Not a real one, because there is no select to raise
     * it from; carrying `target.value` is what lets every call site stay as it
     * was written.
     */
    const emit = React.useCallback(
      (next: string) => {
        if (next === current) return
        if (!controlled) setInner(next)
        if (!onChange) return
        const target = { value: next, name: name ?? "", id: id ?? "" }
        onChange({
          target,
          currentTarget: target,
          type: "change",
          bubbles: true,
          cancelable: false,
          defaultPrevented: false,
          preventDefault: () => {},
          stopPropagation: () => {},
          isDefaultPrevented: () => false,
          isPropagationStopped: () => false,
          persist: () => {},
        } as unknown as React.ChangeEvent<HTMLSelectElement>)
      },
      [controlled, current, id, name, onChange],
    )

    const enabledFrom = React.useCallback(
      (from: number, step: number) => {
        let index = from
        while (index >= 0 && index < options.length && options[index]?.disabled) {
          index += step
        }
        return index >= 0 && index < options.length ? index : -1
      },
      [options],
    )

    const show = React.useCallback(() => {
      // Nothing to show is not a popup. An empty box would otherwise appear
      // while a list of entities is still loading.
      if (options.length === 0) return
      // Which way to open is decided once, on the way open, from where the
      // trigger actually is. The measurement itself is in
      // `popup-placement.ts`, shared with the scripted date picker since
      // 2026-08-04: it is the rule that room is measured inside whatever would
      // clip the popup rather than inside the window, because three of these
      // live in a modal whose body scrolls and an absolutely positioned list
      // is cut off at that body's edge long before the viewport has anything
      // to say about it.
      setDropUp(shouldDropUp(triggerRef.current, LIST_MAX))
      setActive(currentIndex >= 0 ? currentIndex : Math.max(0, enabledFrom(0, 1)))
      setOpen(true)
    }, [currentIndex, enabledFrom, options.length])

    const close = React.useCallback(() => setOpen(false), [])

    // A pointer landing anywhere else closes the list. `pointerdown` rather
    // than `click`, so the list is gone before whatever was clicked reacts.
    React.useEffect(() => {
      if (!open) return
      const onPointerDown = (event: PointerEvent) => {
        if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
      }
      document.addEventListener("pointerdown", onPointerDown)
      return () => document.removeEventListener("pointerdown", onPointerDown)
    }, [open])

    // Two different scrolls, and both are needed. The first brings the list
    // itself into view inside a modal's scrolling body; the second keeps the
    // option being walked visible inside the list's own scroll.
    React.useEffect(() => {
      if (!open) return
      listRef.current?.scrollIntoView?.({ block: "nearest" })
    }, [open])

    React.useEffect(() => {
      if (!open) return
      listRef.current
        ?.querySelector<HTMLElement>('[data-active="true"]')
        ?.scrollIntoView?.({ block: "nearest" })
    }, [open, active])

    // --- Type-ahead ---
    //
    // The buffer accumulates while typing continues and resets after a pause,
    // and a repeated single character cycles through the options starting with
    // it, both of which are what a native select does.
    const typed = React.useRef("")
    const typedTimer = React.useRef<ReturnType<typeof setTimeout>>(undefined)
    React.useEffect(() => () => clearTimeout(typedTimer.current), [])

    const search = React.useCallback(
      (buffer: string, from: number) => {
        const query = buffer.toLowerCase()
        const cycling = query.length > 1 && [...query].every((c) => c === query[0])
        const needle = cycling ? query.slice(0, 1) : query
        const start = cycling ? from + 1 : from
        for (let step = 0; step < options.length; step += 1) {
          const index = (start + step + options.length) % options.length
          const option = options[index]
          if (!option || option.disabled) continue
          if (option.text.trim().toLowerCase().startsWith(needle)) return index
        }
        return -1
      },
      [options],
    )

    const typeAhead = React.useCallback(
      (character: string) => {
        typed.current += character
        clearTimeout(typedTimer.current)
        typedTimer.current = setTimeout(() => {
          typed.current = ""
        }, 500)
        const buffer = typed.current
        if (open) {
          setActive((from) => {
            const found = search(buffer, from)
            return found >= 0 ? found : from
          })
          return
        }
        // Closed, a native select commits the match outright.
        const found = search(buffer, currentIndex < 0 ? 0 : currentIndex)
        const option = found >= 0 ? options[found] : undefined
        if (option) emit(option.value)
      },
      [currentIndex, emit, open, options, search],
    )

    /** Walks the value while the list is shut, which is what Windows does. */
    const stepValue = React.useCallback(
      (step: number) => {
        const from = currentIndex >= 0 ? currentIndex : step > 0 ? -1 : options.length
        const index = enabledFrom(from + step, step)
        const option = index >= 0 ? options[index] : undefined
        if (option) emit(option.value)
      },
      [currentIndex, emit, enabledFrom, options],
    )

    const take = React.useCallback(
      (index: number) => {
        const option = options[index]
        if (!option || option.disabled) return
        emit(option.value)
        setOpen(false)
        typed.current = ""
        triggerRef.current?.focus()
      },
      [emit, options],
    )

    function handleKeyDown(event: React.KeyboardEvent<HTMLButtonElement>) {
      onKeyDown?.(event)
      if (event.defaultPrevented || disabled) return
      const { key, altKey, ctrlKey, metaKey } = event

      // Tab is the one key that must reach the browser: closing and letting
      // focus move on is the whole behaviour.
      if (key === "Tab") {
        setOpen(false)
        return
      }

      const printable = key.length === 1 && !ctrlKey && !metaKey && !altKey

      if (!open) {
        if (key === "Enter" || key === "F4" || (altKey && key === "ArrowDown")) {
          event.preventDefault()
          show()
        } else if (key === " ") {
          event.preventDefault()
          show()
        } else if (key === "ArrowDown") {
          event.preventDefault()
          stepValue(1)
        } else if (key === "ArrowUp") {
          event.preventDefault()
          stepValue(-1)
        } else if (printable) {
          event.preventDefault()
          typeAhead(key)
        }
        return
      }

      switch (key) {
        case "Escape":
          // Closes on the value it opened on. Nothing was committed on the way
          // through, so there is nothing to put back.
          event.preventDefault()
          typed.current = ""
          close()
          return
        case "Enter":
          event.preventDefault()
          take(active)
          return
        case " ":
          event.preventDefault()
          // A space continues a phrase being typed rather than choosing, which
          // is the only way "cost of" ever reaches its option.
          if (typed.current) typeAhead(" ")
          else take(active)
          return
        case "ArrowDown":
          event.preventDefault()
          if (altKey) close()
          else {
            const next = enabledFrom(active + 1, 1)
            if (next >= 0) setActive(next)
          }
          return
        case "ArrowUp":
          event.preventDefault()
          if (altKey) close()
          else {
            const previous = enabledFrom(active - 1, -1)
            if (previous >= 0) setActive(previous)
          }
          return
        case "Home":
          event.preventDefault()
          {
            const firstEnabled = enabledFrom(0, 1)
            if (firstEnabled >= 0) setActive(firstEnabled)
          }
          return
        case "End":
          event.preventDefault()
          {
            const lastEnabled = enabledFrom(options.length - 1, -1)
            if (lastEnabled >= 0) setActive(lastEnabled)
          }
          return
        case "PageDown":
        case "PageUp": {
          event.preventDefault()
          const step = key === "PageDown" ? 10 : -10
          const target = Math.min(Math.max(active + step, 0), options.length - 1)
          const found = enabledFrom(target, step > 0 ? -1 : 1)
          if (found >= 0) setActive(found)
          return
        }
        default:
          if (printable) {
            event.preventDefault()
            typeAhead(key)
          }
      }
    }

    return (
      <span ref={rootRef} className={cn("relative inline-block w-full", className)}>
        <button
          ref={triggerRef}
          type="button"
          id={id}
          role="combobox"
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-controls={listId}
          // Only ever pointed at an option that exists: a dangling IDREF is
          // read by a screen reader as nothing at all.
          aria-activedescendant={open && options[active] ? optionId(active) : undefined}
          aria-autocomplete="none"
          // Before the spread, so `Field`'s own injection wins where a screen
          // sets both.
          aria-required={required || undefined}
          disabled={disabled}
          name={name}
          className={cn(
            field,
            "flex cursor-pointer items-center pr-9 text-left font-normal",
          )}
          onClick={(event) => {
            onClick?.(event)
            if (event.defaultPrevented) return
            if (open) close()
            else show()
          }}
          onKeyDown={handleKeyDown}
          onBlur={(event) => {
            onBlur?.(event)
            if (!rootRef.current?.contains(event.relatedTarget as Node)) setOpen(false)
          }}
          {...rest}
        >
          {/* The selected option's label and nothing else. A trigger showing
              nothing when the value matches no option is what a native select
              does with the same mismatch, and inventing a fallback would hide
              the mismatch rather than surface it.

              `min-w-0` is what lets the ellipsis happen: a flex item will not
              shrink below its content without it, so a long label would spill
              past the chevron instead of truncating. */}
          <span className="min-w-0 truncate">{selected?.label}</span>
        </button>
        <ChevronDown
          className={cn(
            "pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground transition-transform duration-150",
            open && "rotate-180",
          )}
          aria-hidden
        />
        {open && (
          <ul
            id={listId}
            ref={listRef}
            role="listbox"
            // Points at the trigger, whose own name comes from the `<label>`
            // or the `aria-label`, so the list is named without this component
            // ever having to know which of the two a caller used.
            aria-labelledby={id}
            className={cn(
              "absolute left-0 z-50 max-h-64 w-max min-w-full max-w-[calc(100vw-2rem)] overflow-y-auto rounded-lg border bg-popover p-1 text-popover-foreground shadow-elevated",
              dropUp ? "bottom-full mb-1" : "top-full mt-1",
            )}
          >
            {options.map((option, index) => {
              const isSelected = option.value === current
              return (
                <li
                  key={`${option.value}-${index}`}
                  id={optionId(index)}
                  role="option"
                  aria-selected={isSelected}
                  aria-disabled={option.disabled || undefined}
                  data-active={index === active ? "true" : undefined}
                  // Focus must not leave the trigger: it is what carries
                  // `aria-activedescendant`, and a blur would close the list
                  // before the click that opened it resolved.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => take(index)}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-2 text-sm",
                    option.disabled
                      ? "cursor-not-allowed opacity-50"
                      : "hover:bg-muted data-[active=true]:bg-muted",
                    isSelected && "font-medium",
                  )}
                >
                  <Check
                    className={cn(
                      "h-4 w-4 shrink-0 text-primary",
                      !isSelected && "invisible",
                    )}
                    aria-hidden
                  />
                  <span className="min-w-0 flex-1">{option.label}</span>
                </li>
              )
            })}
          </ul>
        )}
      </span>
    )
  },
)
Select.displayName = "Select"

export function Label({ className, ...props }: React.ComponentProps<"label">) {
  return (
    <label
      className={cn(
        "mb-1.5 block text-[13px] font-medium text-foreground/90",
        className,
      )}
      {...props}
    />
  )
}

/**
 * A labelled control with room for the server's message.
 *
 * `error` comes from a 422, never from browser validation: the server decides,
 * and browser checks are convenience only.
 */
export function Field({
  label,
  htmlFor,
  error,
  hint,
  required,
  children,
}: {
  label: string
  htmlFor?: string
  error?: string | null
  hint?: string
  required?: boolean
  children: React.ReactNode
}) {
  const generated = React.useId()
  const controlId = htmlFor ?? generated
  const hintId = `${controlId}-hint`
  const errorId = `${controlId}-error`

  // The control is wired to its label, hint and error here rather than at each
  // call site. Without this a 422 is visually attached to its input and
  // programmatically detached from it, so a screen-reader user tabbing back to
  // the offending field hears nothing at all.
  const described = [hint && !error ? hintId : null, error ? errorId : null]
    .filter(Boolean)
    .join(" ")

  const control = React.isValidElement(children)
    ? React.cloneElement(children as React.ReactElement<Record<string, unknown>>, {
        id: (children.props as { id?: string }).id ?? controlId,
        "aria-describedby": described || undefined,
        "aria-invalid": error ? true : undefined,
        "aria-required": required || undefined,
      })
    : children

  return (
    <div className="mb-4">
      <Label htmlFor={controlId}>
        {label}
        {required && (
          <span className="ml-0.5 text-destructive" aria-hidden>
            *
          </span>
        )}
      </Label>
      {control}
      {hint && !error && (
        <p id={hintId} className="mt-1 text-xs text-muted-foreground">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="mt-1 text-xs text-destructive" role="alert">
          {error}
        </p>
      )}
    </div>
  )
}

// --- Surfaces --------------------------------------------------------------

export function Card({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("card-surface", className)} {...props} />
}

export function CardHeader({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("border-b px-5 py-4", className)} {...props} />
}

export function CardTitle({ className, ...props }: React.ComponentProps<"h2">) {
  // No weight utility: the base heading rule sets 700, and an in-between
  // weight renders differently depending on which display face a machine has.
  return <h2 className={cn("text-base", className)} {...props} />
}

export function CardContent({ className, ...props }: React.ComponentProps<"div">) {
  return <div className={cn("px-5 py-4", className)} {...props} />
}

// --- Badges ----------------------------------------------------------------

/**
 * Status tones, as a tinted surface with a full-strength label.
 *
 * `color-mix` against the page rather than a fixed pastel: the same rule then
 * produces a pale tint on the light theme and a deep one on the dark theme
 * from a single definition, and the text stays the saturated colour in both,
 * which is what keeps the label legible instead of washing it out.
 */
const badge = cva(
  "inline-flex h-6 items-center gap-1.5 whitespace-nowrap rounded-full px-2.5 text-xs font-medium leading-none ring-1 ring-inset",
  {
    variants: {
      tone: {
        neutral: "bg-muted text-muted-foreground ring-border",
        info: "bg-info/12 text-info ring-info/25",
        success: "bg-success/12 text-success ring-success/25",
        warning: "bg-warning/15 text-warning ring-warning/30",
        // Between routine and wrong: staged, confirmed, awaiting something.
        notice: "bg-notice/12 text-notice ring-notice/25",
        danger: "bg-destructive/12 text-destructive ring-destructive/25",
      },
    },
    defaultVariants: { tone: "neutral" },
  },
)

export function Badge({
  className,
  tone,
  children,
  ...props
}: React.ComponentProps<"span"> & VariantProps<typeof badge>) {
  return (
    <span className={cn(badge({ tone }), className)} {...props}>
      {/* The dot repeats the tone in a second channel, so two statuses stay
          told apart at a glance even where the tint is subtle. */}
      <span
        className={cn(
          "h-1.5 w-1.5 shrink-0 rounded-full",
          !tone || tone === "neutral" ? "bg-muted-foreground/60" : "bg-current",
        )}
        aria-hidden
      />
      {children}
    </span>
  )
}

// --- Money -----------------------------------------------------------------

/**
 * An amount, with the R against the left edge of its container and the digits
 * against the right.
 *
 * Two things fall out of that split, and both are the point. Every R sits
 * directly under the R above it, because each is at the left edge of a column
 * of equal width; and the digits stay right-aligned against each other, so the
 * decimal points line up and the eye can compare magnitudes down the column
 * without reading a single number.
 *
 * It fills its container, so the container is what decides the gap: give a
 * table column a width and every row in it agrees automatically. `min-w-24`
 * is only a floor, to stop the two halves colliding in a cramped cell.
 *
 * `tabular-nums` matters as much as the alignment does. Not every face in
 * the body stack has fixed-width figures by default, and a right-aligned
 * column of proportional digits comes out ragged one digit in.
 *
 * Use `cents="auto"` on a standalone headline figure, never in a column: the
 * alignment above is exactly what dropping a `.00` from some rows destroys.
 */
export function Money({
  amount,
  cents = "always",
  className,
}: {
  amount: string | number | null | undefined
  cents?: "always" | "auto"
  className?: string
}) {
  const { symbol, digits } = splitRands(amount, { cents })
  return (
    <span
      className={cn(
        "inline-flex w-full min-w-24 items-baseline justify-between gap-3 tabular-nums",
        className,
      )}
    >
      <span>{symbol}</span>
      <span>{digits}</span>
    </span>
  )
}

// --- Table -----------------------------------------------------------------
//
// Plain primitives. The paginated, server-sorted table every list screen uses
// is `@/components/data-table`, which is built out of these.

export function Table({ className, ...props }: React.ComponentProps<"table">) {
  return (
    <div className="w-full overflow-x-auto">
      <table className={cn("w-full text-left text-sm", className)} {...props} />
    </div>
  )
}

export function Th({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      className={cn(
        "whitespace-nowrap px-4 py-3 text-[11px] font-semibold uppercase tracking-[0.06em] text-muted-foreground",
        className,
      )}
      {...props}
    />
  )
}

export function Td({ className, ...props }: React.ComponentProps<"td">) {
  return (
    <td
      className={cn("border-t border-border px-4 py-3 align-middle", className)}
      {...props}
    />
  )
}

// --- States ----------------------------------------------------------------
//
// Empty, loading and failed each get a designed state. None of them is an
// error toast over a blank table.

export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn("animate-pulse rounded bg-muted", className)}
      aria-hidden
    />
  )
}

/**
 * The one shape a refused save wears across the product: an icon, a sentence,
 * `role="alert"`. Promoted 2026-08-04 when the wizard's banner had been copied
 * into five forms and the copies had started to drift. Render it only when
 * there is something to say - an always-present empty alert announces itself
 * to a screen reader on every render.
 */
export function FormError({
  children,
  className,
}: {
  children: React.ReactNode
  className?: string
}) {
  return (
    <div
      role="alert"
      className={cn(
        "flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2.5 text-sm text-destructive",
        className,
      )}
    >
      <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
      <p>{children}</p>
    </div>
  )
}

export function EmptyState({
  title,
  description,
  icon,
  action,
  compact,
}: {
  title: string
  description?: string
  icon?: React.ReactNode
  action?: React.ReactNode
  compact?: boolean
}) {
  return (
    <div
      className={cn(
        "brand-flow flex flex-col items-center justify-center rounded-2xl px-6 text-center",
        compact ? "py-10" : "py-16",
      )}
    >
      {/* Two soft organic blobs behind the icon: friendlier than a bare
          pictogram, and drawn from the brand tokens so a new client's palette
          carries through. */}
      <div className="relative mb-4 grid h-16 w-16 place-items-center">
        <span
          className="absolute inset-0 rounded-[42%_58%_54%_46%/47%_42%_58%_53%] bg-primary/8"
          aria-hidden
        />
        <span
          className="absolute inset-2 rounded-[58%_42%_46%_54%/53%_58%_42%_47%] bg-warning/10"
          aria-hidden
        />
        <span className="relative text-muted-foreground [&_svg]:h-6 [&_svg]:w-6">
          {icon ?? <Inbox aria-hidden />}
        </span>
      </div>
      <p className="font-display text-base font-bold">{title}</p>
      {description && (
        <p className="mt-1.5 max-w-sm text-sm leading-relaxed text-muted-foreground">
          {description}
        </p>
      )}
      {action && (
        <div className="mt-4 flex flex-wrap items-center justify-center gap-2">
          {action}
        </div>
      )}
    </div>
  )
}

export function ErrorState({
  title = "That did not load",
  description,
  onRetry,
}: {
  title?: string
  description?: string
  onRetry?: () => void
}) {
  return (
    <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
      <AlertCircle className="mb-3 h-8 w-8 text-destructive" aria-hidden />
      <p className="font-medium">{title}</p>
      {description && (
        <p className="mt-1 max-w-sm text-sm text-muted-foreground">
          {description}
        </p>
      )}
      {onRetry && (
        <Button variant="secondary" className="mt-4" onClick={onRetry}>
          Try again
        </Button>
      )}
    </div>
  )
}

/**
 * The two scales a band comes in, in WHOLE PIXELS.
 *
 * The rule is a ratio off the title the band carries (rim 0.3, box = rim + 1em
 * of text + rim, sweep 1.11 x the box), and the title is the one number a
 * surface chooses. What ships is that rule rounded to whole pixels per scale,
 * which is the fix for a defect Dawid caught in the running app on 2026-08-01:
 * raw em arithmetic reaches the rasteriser as fractions (at a 33px title the
 * box is 52.8px, the rim 9.9px, the sweep 58.608px), and an SVG rounds its own
 * rim to a different whole pixel than the div beside it, which shows as a step
 * and a seam where the joint meets the rim.
 *
 *   page    21px title  ->  rim  6 (0.3 x 21 = 6.3)   box 33 (6+21+6)
 *                           sweep 37 (1.11 x 33 = 36.63)
 *   dialog  20px title  ->  rim  6 (0.3 x 20 = 6.0)   box 32 (6+20+6)
 *                           sweep 36 (1.11 x 32 = 35.52)
 *
 * The page title is not a taste number any more. Dawid ruled on 2026-08-02
 * that the band title is THE SAME SIZE AS THE WORDMARK, so it is derived:
 * measured in the rendered app (tools\measure-band-lockup.mjs), Arial Nova
 * Cond Bold carries 0.714 caps per px, the wordmark's letters are 0.896 of
 * its image box, and the lockup ratio in `__root.tsx` is set so the two cap
 * heights are equal at any base. 21px is the base at which the wordmark
 * keeps the physical size it had before the ruling. (History: 33px at the
 * mock, 28px after the first PDF review, 21px derived.) The dialog scale is
 * the 20px WCAG floor and cannot follow a ruling below it.
 *
 * It is TypeScript rather than a stylesheet because 20px is a contrast floor,
 * not a taste. White on the CI red is 4.12:1, which clears AA only as large
 * text, and large text is 18.66px once it is bold (the global heading rule
 * supplies the 700). A floor that lives only in CSS is a floor no test suite
 * can hold, so it lives here and `ui.test.tsx` holds it.
 */
export const BAND_SCALE = {
  page: { title: 21, rim: 6, box: 33, sweep: 37 },
  dialog: { title: 20, rim: 6, box: 32, sweep: 36 },
} as const

export type BandSize = keyof typeof BAND_SCALE

/**
 * The custom properties a surface needs in order to wear a band at `size`.
 *
 * Exported because the page footer is the same shape rotated into the bottom
 * corner and has to be drawn at the same numbers; taking them from here is
 * what stops the two ends of a page drifting apart, which they had before.
 */
export function bandStyle(size: BandSize): React.CSSProperties {
  const scale = BAND_SCALE[size]
  return {
    fontSize: `${scale.title}px`,
    "--band-rim": `${scale.rim}px`,
    "--band-h": `${scale.box}px`,
    "--band-sweep": `${scale.sweep}px`,
  } as React.CSSProperties
}

/**
 * The rim-ruled corner block: the banner every page header, every dialog and
 * the sign-in card wears, and the shape the page footer is a rotation of.
 *
 * Dawid's ruling, 2026-08-01, mock of record at
 * `docs\design\2026-08-01-banner-mock-pages.html`. It replaces the previous
 * band, which fixed both
 * banners at 92px whatever type they carried and laid a full-width rim over
 * the whole row.
 *
 * The construction is three parts in a row, and the order is the design:
 *
 *   1. the red BLOCK, `--band-h` tall (rim + 1em of title + rim), its title
 *      inset from the left by the surface's own content padding
 *   2. the S JOINT, one cubic at full ease, `--band-sweep` wide
 *   3. the RIM, `--band-rim` tall, flexing out to the surface's right edge
 *
 * The rim is born at the block's trailing edge together with the curve, which
 * is what makes this a corner block rather than a band with a stripe on it.
 * Nothing is layered over the block, and there is no absolute positioning in
 * the row at all bar the trailing slot.
 *
 * A dialog is a page's smaller sibling here, not a second piece of furniture:
 * `size` picks the type and the geometry follows it down.
 */
export function SignageBand({
  title,
  titleId,
  as: Heading = "h1",
  size = "page",
  trailing,
}: {
  title: string
  /** For a dialog, which labels itself by pointing at its own title. */
  titleId?: string
  /** `h1` for a page, `h2` inside a dialog, so the outline stays sane. */
  as?: "h1" | "h2"
  /** Which type size the band is drawn off. Everything else is em from it. */
  size?: BandSize
  /** Sits clear of the band's red, on the surface below the rim. */
  trailing?: React.ReactNode
}) {
  const scale = BAND_SCALE[size]
  return (
    <div
      style={bandStyle(size)}
      className="relative flex items-stretch"
      data-signage-band={size}
      data-title-px={scale.title}
    >
      <div className="flex h-[var(--band-h)] min-w-0 items-center bg-brand py-[var(--band-rim)] pr-[var(--band-rim)] pl-[var(--column-inset,var(--band-rim))]">
        {/* All caps, as the signage sets its own messages. `leading-none`
            because the box is arithmetic on the line box (rim + 1em + rim),
            and the global 1.16 heading leading would put slack in it.
            `truncate` is the one thing the mock has no need of: its titles are
            short, while this product has event names that would otherwise
            push the joint off the surface. Caps have no descenders to clip. */}
        <Heading
          id={titleId}
          className="truncate text-[1em] uppercase leading-none text-brand-foreground"
        >
          {title}
        </Heading>
      </div>
      {/* Each part overlaps the one before it by a pixel. Same fill, so the
          overlap is invisible, and it is what closes the hairline gap that a
          sub-pixel rounding difference opens between an SVG edge and the div
          beside it. */}
      <ScurveJoint size={size} />
      <div
        className={cn(
          "-ml-px h-[var(--band-rim)] flex-1 bg-brand",
          // Keeps the trailing slot off the red when a long title has eaten
          // the row: 3em is comfortably wider than the control that sits there.
          trailing && "min-w-[3em]",
        )}
        aria-hidden
      />
      {trailing && (
        // Absolute, so the rim still reaches the surface's edge behind it, and
        // inset below the rim so the control sits on the surface rather than
        // on the red: an icon is not large text, and could not clear AA there.
        <div className="absolute bottom-0 right-[var(--band-rim)] top-[var(--band-rim)] flex items-center">
          {trailing}
        </div>
      )}
    </div>
  )
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  /** The section the screen belongs to, worded as the navigation words it. */
  eyebrow?: string
  title: string
  description?: string
  actions?: React.ReactNode
}) {
  return (
    <div className="mb-6">
      {/* The band is the surface's own corner, so it bleeds out of the content
          column to the page's edges, and the title's inset brings the type
          back to the column. The three values are `main`'s own
          `px-4 sm:px-6 lg:px-8` in `__root.tsx`, restated because CSS gives an
          element no way to ask its parent what padding it has. */}
      <div
        className="rise-in -mx-4 [--column-inset:1rem] sm:-mx-6 sm:[--column-inset:1.5rem] lg:-mx-8 lg:[--column-inset:2rem]"
        role="presentation"
      >
        <SignageBand title={title} />
      </div>
      {(eyebrow || description || actions) && (
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <div className="min-w-0">
            {eyebrow && <p className="section-label mb-1">{eyebrow}</p>}
            {description && (
              <p className="max-w-2xl text-sm leading-relaxed text-muted-foreground">
                {description}
              </p>
            )}
          </div>
          {actions && <div className="flex gap-2">{actions}</div>}
        </div>
      )}
    </div>
  )
}

/**
 * The joint between the block and the rim, and the shape the page footer's
 * sweep is the same drawing rotated.
 *
 * One cubic Bezier with both control points level with their own endpoint,
 * which is what makes the curve leave the block's foot horizontally and land
 * on the rim's underside horizontally. The one free number in such a curve is
 * how far along the run the controls sit, and Dawid ruled it at full ease on
 * 2026-08-01: a = 0.5, both controls at half the sweep.
 *
 * The viewBox is the scale's own pixels, so one user unit is one CSS pixel and
 * the SVG's rim edge lands on exactly the pixel the rim strip beside it does.
 * Drawing it in some other unit and scaling is what put a step at this joint.
 *
 * `-ml-px` overlaps the block by a pixel. The overlap is the same red on both
 * sides, so it cannot be seen, and it closes the seam a rounding difference
 * would otherwise open between two boxes that merely abut.
 */
function ScurveJoint({ size }: { size: BandSize }) {
  const { rim, box, sweep } = BAND_SCALE[size]
  return (
    <svg
      viewBox={`0 0 ${sweep} ${box}`}
      preserveAspectRatio="none"
      className="-ml-px h-[var(--band-h)] w-[var(--band-sweep)] shrink-0 text-brand"
      fill="currentColor"
      aria-hidden
    >
      <path
        d={`M0 0 H${sweep} V${rim} C${sweep / 2} ${rim} ${sweep / 2} ${box} 0 ${box} Z`}
      />
    </svg>
  )
}
